<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import { ChangelogSection, ColoredButton, TableTitle } from '@/shared/ui';
  import { clearBreadcrumbLabel, setBreadcrumbLabel, useCustomToast } from '@/shared/lib';
  import PencilIcon from '@/shared/assets/icons/PencilIcon.svg';
  import RecordInfoFields from './RecordInfoFields.vue';
  import { getRejectedMatrix, type RejectedMatrixDetailResponse } from '../api/matrices.api';
  import {
    buildApprovalStageInfo,
    buildElaborationInfo,
    buildVerificationStageInfo,
  } from '../lib/rejected-record-info';

  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();

  const record = ref<RejectedMatrixDetailResponse | null>(null);
  const isLoading = ref(true);

  const recordId = computed(() => Number(route.params.id));
  const isRejectedInVerification = computed(
    () => record.value?.rejectedStage === 'VERIFICATION'
  );

  async function fetchRecord(): Promise<void> {
    isLoading.value = true;
    try {
      const result = await getRejectedMatrix(recordId.value);
      record.value = result;
      setBreadcrumbLabel(result.code);
    } catch (error) {
      console.error('Error fetching rejected matrix:', error);
      record.value = null;
      showErrorToast({
        title: 'Erro ao carregar registro reprovado',
        type: 'error',
        hideIcon: true,
      });
      router.push({ name: 'GestaoRegistrosModelos', query: { tab: 'reprovados' } });
    } finally {
      isLoading.value = false;
    }
  }

  function editRecord(): void {
    router.replace({
      name: 'EditarFormulario',
      params: { id: String(recordId.value) },
      query: { origem: 'reprovados' },
    });
  }

  onMounted(fetchRecord);
  watch(recordId, fetchRecord);

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div
      v-if="isLoading"
      class="flex h-[80vh] items-center justify-center"
      data-testid="view-rejected-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <template v-else-if="record">
      <h1 class="text-left text-xl/7.5 font-semibold text-gray-07">
        Registro reprovado
      </h1>

      <section
        class="overflow-x-auto rounded-2xl border border-gray-02 bg-white-01 text-gray-07 divide-y divide-gray-02"
        data-testid="view-rejected-card"
      >
        <div class="w-full py-4.5 pl-6 text-left">
          <TableTitle text="Registro" />
        </div>

        <div
          class="flex flex-col gap-4 p-6"
          data-testid="view-rejected-elaboration"
        >
          <h2 class="text-left font-semibold">
            Elaboração:
          </h2>
          <RecordInfoFields :info="buildElaborationInfo(record)" />
        </div>

        <ChangelogSection :changelog="record.changelog" />

        <div
          class="flex flex-col gap-4 p-6"
          data-testid="view-rejected-approval"
        >
          <h2 class="text-left font-semibold">
            Etapa de Análise Crítica e Aprovação:
          </h2>
          <RecordInfoFields :info="buildApprovalStageInfo(record)" />
        </div>

        <div
          v-if="isRejectedInVerification"
          class="flex flex-col gap-4 p-6"
          data-testid="view-rejected-verification"
        >
          <h2 class="text-left font-semibold">
            Etapa de Verificação e Vigência:
          </h2>
          <RecordInfoFields :info="buildVerificationStageInfo(record)" />
        </div>

        <div class="flex flex-col gap-4 p-6">
          <h2 class="text-left font-semibold">
            Justificativa:
          </h2>
          <p
            class="min-h-33 whitespace-pre-line break-words rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-left"
            data-testid="view-rejected-justification"
          >
            {{ record.justification ?? '-' }}
          </p>
          <ColoredButton
            class="self-end"
            text="Editar Registro"
            :icon="PencilIcon"
            icon-alt="Editar Registro"
            data-testid="view-rejected-edit"
            @click="editRecord"
          />
        </div>
      </section>
    </template>
  </div>
</template>
